<template>
    <div> 
        <div class="form-box-title title-with-right-part" v-if="showTopPanel">
            <span>
                {{ richEditState && allowEditRichText ? $t("Редактирование") : $t("Предпросмотр") }} {{ attachment ? '(' + attachmentName + ')': '' }}
                <v-icon 
                    v-if="useRichText && richEditState && allowEditRichText" 
                    class="fbt-act-btn" 
                    role="button" 
                    v-tooltip.top-center='$t("Переименовать_файл")' 
                    small 
                    left
                    @click="renameAttachmentClick" 
                >
                    fas fa-edit
                </v-icon>
            </span>
            
            <div class="twrp-content">
                <v-chip
                    v-if="isPaperCopyDocVisible && !isNewFile"
                    class="table-label mr-2"
                    small
                    label
                    color="#009c95"
                    dark
                    @click="loadPaperCopy"
                >
                    <div class="d-flex align-center">
                        <template>
                            <v-icon>fa-regular fa-pen-to-square</v-icon>
                            {{$t("Бумажная_копия_документа")}}
                        </template>
                    </div>
                </v-chip>
                <v-chip
                    v-if="useRichText && allowEditRichText"
                    class="table-label"
                    small
                    label
                    color="#009c95"
                    dark
                    @click="richEditMode"
                >
                    <div class="d-flex align-center" v-if="allowEditRichText">
                        <template v-if="!isNewFile">
                            <template v-if="!richEditState">
                                <v-icon>fa-regular fa-pen-to-square</v-icon>
                                {{$t("Редактировать")}}
                            </template>
                            <template v-else>
                                <v-icon>fa-regular fa-file-fragment</v-icon>
                                {{$t("Предпросмотр")}} 
                            </template>
                        </template>
                        <template v-else>
                            <v-icon>fa-regular fa-ban</v-icon>
                            {{$t("Отмена")}}
                        </template>
                    </div>
                </v-chip>
            </div>
        </div>
        <v-progress-linear
            v-show="isPreviewLoading"
            height="2" 
            color="#E43958" 
            indeterminate
        >
        </v-progress-linear>
        <div id="PDFPreview" class="file-preview-iframe" v-show="!isPreviewLoading">
            <div class="cfl-placeholder" v-if="noPreviewMessage">{{ noPreviewMessage }}</div>
            
            <template v-else-if="attachment">
                <RichTextEditor
                    v-if="useRichText"
                    :key="richTextEditorKey"
                    ref="richTextComponent"
                    :content="currentContent"
                    :fileName="attachmentName"
                    :extension="attachmentExtension"
                    :readOnly="!richEditState"
                    :isFullscreen="isFullscreen"
                    :editorHeight="calculatedEditorHeight"
                    @save="richEditSave"
                    @toggle-fullscreen="toggleFullscreen"
                />
                <PDFTabViewer
                    v-else-if="usePDFViewerText"
                    :key="'pdf' + attachment.Id"
                    :idAttach="attachment.Id"
                    :nameAttach="attachmentName"
                    :url="previewSrc"
                />
                <iframe
                    v-else
                    :key="'image' + attachment.Id"
                    frameBorder="0"
                    :src="previewSrc"
                >
                </iframe>
            </template>
            
            <div v-else class="cfl-placeholder" >{{$t('Вложения_не_выбраны')}}</div>

        </div>
    </div>
</template>

<script>
import Axios from 'axios';
import { httpAPI } from "@/api/httpAPI";
import { mapActions, mapGetters } from 'vuex';
import sys from '@/services/system';
import RichTextEditor from '@/components/global/RichTextEditor';
import PDFTabViewer from '@/components/global/PDFTabViewer'

export default {
    name: "FilePreview",
    components: {
        RichTextEditor,
        PDFTabViewer
    },
    props: {
        allowEdit: {
            type: Boolean,
            default: false
        },
        calculatedEditorHeight: {
            type: String,
            default: '500px'
        },
        showTopPanel: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            attachment: null,
            noPreviewMessage: null,
            isPreviewLoading: false,
            previewSrc: '',
            previewCache: [],
            prevAttchName: '',
            // для richText editor
            currentContent: '',
            isFullscreen: false,
            richTextEditorKey: '',
            richEditFormats: [".DOCX",".RTF",".TXT"],
            pdfViewerFormats: [".PDF",".DOC",".XLS",".XLSX",".PPT",".PPTX",".XML",".ODT",".ODS",".ODP",".CSV"],
            imageViewerFormats: [".JPG",".JPEG",".PNG"],
            paperCopyFormats: [".DOCX",".DOC"]
        }
    },
    computed: {
        ...mapGetters('global/actionsource', { documentSource: 'getDataSource', dataSourceType: 'getDataSourceType' }),
        ...mapGetters('global/attachments',{ docAttachments: 'getDataSource' }),
        allowEditRichText(){
            return this.allowEdit || (this.$store.getters['documents/toolbar/getMenu']?.find(b => b.Action === "File") ?? false);
        },
        attachmentName(){
            return this.attachment?.Name;
        },
        attachmentExtension(){
            if (this.attachment){
                if (this.attachment.Extension)
                    return this.attachment.Extension.toUpperCase();
                else if (this.attachmentName)
                    return this.attachmentName.match(/\.([^.]+)$|$/)[0]?.toUpperCase() ?? '';
            }
            return '';
        },
        isRegistered(){
            return this.documentSource?.Data?.Object?.Document != null && [1,2].includes(this.documentSource?.Data?.Object?.Document?.RegState); 
        },
        isPaperCopyDocVisible(){
            return this.paperCopyFormats.includes(this.attachmentExtension) && (this.dataSourceType == "OutgoingDocument" || this.dataSourceType == "IncomingDocument") && this.isRegistered;
        },
        supportedFormats(){
            return [...this.pdfViewerFormats,...this.richEditFormats,...this.imageViewerFormats];
        },
        useRichText(){
            return this.richEditFormats.includes(this.attachmentExtension);
        },
        usePDFViewerText(){
            return this.pdfViewerFormats.includes(this.attachmentExtension);
        },
        richEditState:{
            get: function() {
                return this.$store.getters[`global/filePreview/getRichEditState`];
            },
            set: function(v) {
                this.$store.commit(`global/filePreview/SET_RICHEDITSTATE`, v);
            }
        },
        isNewFile(){
            return this.attachment?.Id === 'newAttachment';
        }
    },
    watch: {
        attachment(val){
            if (val)
                this.loadPreview(val.Id)
        },
        calculatedEditorHeight(){
            this.applyHeight();
        },
    },
    methods: {
        ...mapActions('global/attachments', ['updateAttachment', 'onFilePicked', 'renameAttachment']),
        setAttachment(att){
            this.attachment = att;
        },
        async loadPaperCopy() {
            if (!this.attachment) return;
            if (this.isPaperCopyDocVisible){
                this.$eventBus.$emit('on-download-file', { id: this.attachment.Id, name: this.attachmentName, passedType: 'AttachmentPaperCopy' });
            }
            else {
                this.$notify.alert("Невозможно_для_данного_типа_файла");
            }
        },
        async loadPreview(attachmentId) {
            this.previewSrc = "";
            if ( !attachmentId || this.attachment?.Id != attachmentId) return;

            if (this.attachment && !this.supportedFormats.includes(this.attachmentExtension)) {
                this.noPreviewMessage = this.$t('Предпросмотр_для_данного_типа_файла_невозможен!');
                return;
            }

            this.noPreviewMessage = null;
            this.isPreviewLoading = true;
            if (!this.isNewFile){
                let found = this.previewCache.find(x => x.id == attachmentId);

                if (this.cancellationTokenSorce)
                    this.cancellationTokenSorce.cancel(`$Предпросмотр_вложения_<${this.prevAttchName}}>_отменен.message`);

                this.prevAttchName = this.attachment.Name;
                
                if (found != undefined) {
                    if (this.useRichText) {
                        this.richEditSetContent(found.data);
                        this.$emit('height-control-method');
                    } else {
                        this.previewSrc = found.url;
                    }
                }
                else {      
                    this.cancellationTokenSorce = Axios.CancelToken.source();

                    // LibreOffice convertion
                    let url = `api/download/preview?id=${attachmentId}&extension=${this.attachmentExtension}&colontitules=false`;
                    
                    let response = await httpAPI({
                        url: url,
                        method: 'GET',
                        headers: { 'isCommon': this.$store.getters['global/actionsource/isDataSourceCommon'], 'isMeeting': this.dataSourceType === 'Meeting' },
                        skipErrorHandler: true,
                        cancelToken: this.cancellationTokenSorce.token,
                    });
                    
                    if (!response || this.attachment?.Id != attachmentId)
                        return;

                    //response - это ответ со строкой base64
                    if (response.status === 200 && response.data.Success) {
                        if (this.useRichText)
                        {
                            this.richEditSetContent(response.data.Payload);
                            this.previewCache.push({ id: attachmentId, data: response.data.Payload });
                        }
                        else {
                            try
                            {
                                let type = '';
                                switch (this.attachmentExtension)
                                {
                                    case ".JPG":
                                    case ".JPEG":
                                        type = "image/jpg";
                                    break;

                                    case ".PNG":
                                        type = "image/png";
                                    break;

                                    default:
                                        type = "application/pdf";
                                        break;
                                }

                                let url = window.URL.createObjectURL(this.b64toBlob(response.data.Payload, type) ) + '#view=fitH'; //URL с указанием на объект (всегда короткий), внутри данные

                                this.previewCache.push({ id: attachmentId, url }); //оптимизация, при повторном открытии предпросмотра не будем скачивать
                                
                                this.previewSrc = url;
                            }
                            catch(err)
                            {
                                console.log("Ошибка подготовки предпросмотра: ", err);
                            }
                        }
                    }
                    else
                    {
                        this.$notify.alert(response.data.Message);
                        this.noPreviewMessage = this.$t('Предпросмотр_для_данного_файла_невозможен!');
                    }
                }
            }
            else {
                this.richEditSetContent('');
            }
            this.isPreviewLoading = false;
            this.$nextTick(() => {
                this.$emit('height-control-method');
            });
        },
        applyHeight() {
            //var parent = document.getElementsByClassName('content-box')[0];
            var file_preview_iframe_obj = document.getElementsByClassName('file-preview-iframe');
            file_preview_iframe_obj[0].style.height = this.calculatedEditorHeight;
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen
            this.$emit('togille-fullscreen-for-attachment')
        },
        richEditSetContent(content){
            this.currentContent = content;
            this.richTextEditorKey = this.attachment.Id + this.richEditState;
        },
        richEditMode(){
            this.richEditState=!this.richEditState;
            if(this.isNewFile){
                this.richEditSetContent('');
                this.attachment = null;
            }
            else{
                let preview = this.previewCache.find(p => p.id === this.attachment.Id);
                if (preview)
                    this.richEditSetContent(preview.data);
                else
                    this.richEditSetContent('');
            }
        },
        async richEditSave(arrayBuffer) {
            if (this.isNewFile){
                const file = new File([arrayBuffer], this.attachmentName, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', lastModified: Date.now()})
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                const e = {
                    target: {
                        files: dataTransfer.files, // FileList с файлом
                        value: `C:\\${this.attachmentName}`, // Эмуляция пути (как в браузере)
                        name: 'fileInput', // Если используется в форме
                        type: 'file'
                    },
                }
                await this.onFilePicked({e});
                //this.previewCache.splice(this.previewCache.indexOf(preview), 1);
            }
            else {
                if (await this.updateAttachment({ arrayBuffer: arrayBuffer, attachmentId: this.attachment.Id })){
                    let preview = this.previewCache.find(p => p.id === this.attachment.Id);
                    if (preview)
                        // Используем метод компонента для обновления кэша
                        preview.data = await this.$refs.richTextComponent.exportBase64();
                }
            }
        },
        b64toBlob (b64Data, contentType='', sliceSize=512) {
            let byteCharacters = atob(b64Data);
            let byteArrays = [];

            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                let slice = byteCharacters.slice(offset, offset + sliceSize);

                let byteNumbers = new Array(slice.length);

                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            return new Blob(byteArrays, { type: contentType });
        },
        richEditResetStates(){
            this.richEditState = false;
            this.isFullscreen = false;
        },
        async renameAttachmentClick(){
            let oldFileName = '';
            let fileName = '';
            if (this.attachmentName.toLowerCase().endsWith(this.attachmentExtension.toLowerCase())) {
                oldFileName = this.attachmentName.slice(0, -this.attachmentExtension.length);
            }
            
            try {
                fileName = await this.$store.dispatch('global/dialogs/input_box/open', {
                    title: 'Введите_новое_имя_файла', 
                    fieldname: 'Имя_файла',
                    buttontext: 'Принять',
                    oldvalue: oldFileName,
                    requiredval: true
                },{ root: true });
            }
            catch{
                console.log("Отмена");
                return;
            }
            if (fileName && fileName != oldFileName){
                fileName = `${fileName}${this.attachmentExtension.toLowerCase()}`
                if (this.docAttachments.find(f => f.Name == fileName)){
                    this.$notify.alert("Уже_есть_вложение_с_таким_именем");
                }
                else {
                    const fileNameErr = sys.checkFileName(fileName);
                    if (fileNameErr && Array.isArray(fileNameErr) && fileNameErr.length > 0){
                        this.$notify.alert(fileNameErr.join('\n'));
                    }
                    else {
                        await this.renameAttachment({attachmentId: this.attachment.Id, newName: fileName})
                    }
                }
            }
        },
        async createNewFile(){
            let fileName = '';
            try {
                fileName = await this.$store.dispatch('global/dialogs/input_box/open', {
                    title: 'Введите_имя_файла', 
                    fieldname: 'Имя_файла',
                    buttontext: 'Принять',
                    requiredval: true,
                    in_one_line: true
                },{ root: true });
            }
            catch{
                console.log("Отмена");
            }
            if (fileName){
                fileName = `${fileName}.docx`
                if (this.docAttachments.find(f => f.Name == fileName)){
                    this.$notify.alert("Уже_есть_вложение_с_таким_именем");
                }
                else {
                    const fileNameErr = sys.checkFileName(fileName);
                    if (fileNameErr && Array.isArray(fileNameErr) && fileNameErr.length > 0){
                        this.$notify.alert(fileNameErr.join('\n'));
                    }
                    else {
                        this.$store.commit('global/attachments/SET_SELECTED_ATTACHMENT_ID', null);
                        this.$nextTick(() => {
                            this.attachment = {Id: 'newAttachment', Name: fileName};
                            this.richEditState = true;
                        });
                    }
                }
            }
        }
    },
    mounted() {        
        this.$eventBus.$on('create_new_file', async () => {
            await this.createNewFile();
        });
    },
    beforeDestroy() {
        this.$eventBus.$off('create_new_file');
    },
    destroyed() {
        this.previewCache.filter(x => x.url).forEach(x => window.URL.revokeObjectURL(x.url));
        this.previewCache = [];                    
    },
}
</script>